import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Comment, PaginatedResponse } from '@/types/api';

export type CommentTargetType = 'file' | 'file_request';

function fileCommentsKey(fileId: string | undefined) {
  return ['comments', 'file', fileId] as const;
}

function requestCommentsKey(requestId: string | undefined) {
  return ['comments', 'request', requestId] as const;
}

export function useFileComments(fileId: string | undefined) {
  return useQuery({
    enabled: !!fileId,
    queryKey: fileCommentsKey(fileId),
    queryFn: async () =>
      (await api.get<PaginatedResponse<Comment>>(`/files/${fileId}/comments`)).data,
  });
}

export function useCreateFileComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { fileId: string; body: string }) => {
      const { data } = await api.post<Comment>(`/files/${vars.fileId}/comments`, {
        body: vars.body,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: fileCommentsKey(vars.fileId) });
    },
  });
}

export function useRequestComments(requestId: string | undefined) {
  return useQuery({
    enabled: !!requestId,
    queryKey: requestCommentsKey(requestId),
    queryFn: async () =>
      (await api.get<PaginatedResponse<Comment>>(`/requests/${requestId}/comments`)).data,
  });
}

export function useCreateRequestComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; body: string }) => {
      const { data } = await api.post<Comment>(`/requests/${vars.requestId}/comments`, {
        body: vars.body,
      });
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: requestCommentsKey(vars.requestId) });
    },
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      commentId: string;
      targetType: CommentTargetType;
      targetId: string;
    }) => {
      await api.delete(`/comments/${vars.commentId}`);
    },
    onSuccess: (_d, vars) => {
      if (vars.targetType === 'file') {
        qc.invalidateQueries({ queryKey: fileCommentsKey(vars.targetId) });
      } else {
        qc.invalidateQueries({ queryKey: requestCommentsKey(vars.targetId) });
      }
    },
  });
}
